import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { parseISO } from 'date-fns';
import _cloneDeep from 'lodash/cloneDeep';
import _isNil from 'lodash/isNil';
import { persistedQcRunsState } from '@Data/browserPersistence/Reducer';
import { RootState } from '@Services/redux/Store';
import { IFolder } from './Interfaces';
import { initialFolderState } from './State';

const FolderSlice = createSlice({
    name: 'Folder',
    initialState: initialFolderState,
    reducers: {
        reset() {
            return initialFolderState;
        },
        clearSelectedState(state) {
            state.visibleFolders = [];
            state.selectedFolderId = null;
            state.zoomToFolderBoundsId = null;
            state.selectedFolderIds = [];
        },
        updateFolders(state, action) {
            state.folders = action.payload;
        },
        setMultipleFoldersVisible(state, action) {
            state.visibleFolders = action.payload;
        },
        toggleFolderVisibility(state, action) {
            const index = state.visibleFolders.findIndex(
                (wp) => wp.folder_id === action.payload.folder_id
            );
            if (index > -1) {
                state.visibleFolders.splice(index, 1);
            } else {
                state.visibleFolders.push(action.payload);
            }
        },
        setAllFoldersVisibility(state, action) {
            state.visibleFolders = action.payload === true ? state.folders : [];
        },
        clearVisibleFolders(state) {
            state.visibleFolders = [];
        },
        setFolderFields(state, action) {
            state.folderFields = action.payload;
        },
        setZoomToFolderBoundsId(state, action) {
            state.zoomToFolderBoundsId = action.payload;
        },
        setSelectedFolderId(state, action) {
            state.selectedFolderId = action.payload;
        },
        tryRefreshData(state) {
            state.refreshIndex = Math.random();
        },
        setSelectedFolderIds(state, action: PayloadAction<Array<string>>) {
            state.selectedFolderIds = action.payload;
        },
        setExpandedFolderIds(state, action: PayloadAction<Array<string>>) {
            state.expandedFolderIds = action.payload;
        },
        setLoadingFolders(state, action: PayloadAction<boolean>) {
            state.isLoadingFolders = action.payload;
        },
    },
});

export const FolderActions = FolderSlice.actions;

export const foldersState = (state: RootState): Array<IFolder> | null =>
    state.folder.folders;
export const isLoadingFoldersState = (state: RootState): boolean =>
    state.folder.isLoadingFolders;

export const qcFoldersState = createSelector(
    foldersState,
    persistedQcRunsState,
    (folders, qcRuns) => {
        if (_isNil(folders)) {
            return [];
        }

        return folders.map((folder) => {
            const clone = folder;
            let syncDeltaString = 0;
            if (folder.last_io_sync_time) {
                const syncDelta =
                    Date.now() - Date.parse(clone.last_io_sync_time);
                syncDeltaString = Math.floor(syncDelta / 60000);
            }

            const due_date = clone.due_date ? parseISO(clone.due_date) : null;
            const target_date = clone.target_date
                ? parseISO(clone.target_date)
                : null;

            const qcRunIndex = qcRuns?.some(
                (qcRun) => qcRun.folder_id === folder.folder_id
            );

            const obj = {
                id: clone.folder_id,
                folder: clone,
                folder_last_modified: clone.last_modified,
                folder_last_io_sync_time: syncDeltaString,
                folder_due_date: due_date,
                folder_target_date: target_date,
                isInProgress: qcRunIndex,
                ...clone,
            };
            return obj;
        });
    }
);

export const selectedFolderIdsState = (state: RootState): Array<string> =>
    state.folder.selectedFolderIds;

export const visibleFoldersState = (state: RootState): Array<IFolder> =>
    state.folder.visibleFolders;
export const expandedFolderIdsState = (state: RootState): Array<string> =>
    state.folder.expandedFolderIds;
export const folderFieldsState = (state: RootState) =>
    state.folder.folderFields;
export const setZoomToFolderBoundsIdState = (state: RootState) =>
    state.folder.zoomToFolderBoundsId;
export const selectedFolderIdState = (state: RootState) =>
    state.folder.selectedFolderId;

export const selectedFolderNameState = createSelector(
    foldersState,
    selectedFolderIdState,
    (folders, id) => {
        const folder = folders?.find((f) => f.folder_id === id);
        return _isNil(folder) ? '' : folder.folder_name;
    }
);
export const selectedFolderJobIdState = createSelector(
    foldersState,
    selectedFolderIdState,
    (folders, id) => {
        const folder = folders?.find((f) => f.folder_id === id);
        return _isNil(folder) ? '' : folder.job_id;
    }
);

export const selectedFolderState = createSelector(
    foldersState,
    selectedFolderIdState,
    (folders, folderId) => folders?.find((f) => f.folder_id === folderId)
);

export const refreshIndexState = (store: RootState) =>
    store.folder.refreshIndex;

export default FolderSlice.reducer;
